import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/api/client";
import { toast } from "sonner";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [promotingId, setPromotingId] = useState(null);

  const load = () => api.get("/admin/users").then((res) => setUsers(res.data));

  useEffect(() => {
    load();
  }, []);

  const handlePromote = async (id) => {
    setPromotingId(id);
    try {
      await api.post(`/admin/users/${id}/promote`);
      await load();
      toast.success("User promoted to Unlimited");
    } catch (e) {
      toast.error("Could not promote this user");
    } finally {
      setPromotingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] text-white p-4 sm:p-8" data-testid="admin-users-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck className="text-blue-400" size={20} />
          <h1 className="font-heading text-xl font-bold">Users</h1>
        </div>

        <div className="rounded-xl border border-slate-800 overflow-x-auto" data-testid="admin-users-list">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Email</th>
                <th className="text-left px-4 py-2.5">Tier</th>
                <th className="text-left px-4 py-2.5">Verified</th>
                <th className="text-left px-4 py-2.5">Created</th>
                <th className="text-right px-4 py-2.5">Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-t border-slate-800">
                  <td className="px-4 py-2.5">{u.name || "-"}</td>
                  <td className="px-4 py-2.5">{u.email}</td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs ${
                        u.plan === "pro" ? "bg-blue-500/15 text-blue-400" : "bg-slate-700 text-slate-400"
                      }`}
                      data-testid={`user-tier-badge-${u.id}`}
                    >
                      {u.plan === "pro" ? "Unlimited" : "Free"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={u.email_verified ? "text-emerald-400" : "text-slate-500"}>
                      {u.email_verified ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-slate-400">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-2.5 text-right">
                    {u.plan !== "pro" && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`promote-user-button-${u.id}`}
                        disabled={promotingId === u.id}
                        onClick={() => handlePromote(u.id)}
                        className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
                      >
                        Promote to Unlimited
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500" data-testid="admin-users-empty">
                    No users yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
